/**
 * Scout's in-app path builders, one per addressable location in its route table
 * (see `ScoutRoutes.tsx`).
 *
 * Every path is built here rather than interpolated at each call site so the customer and app
 * segments cannot be forgotten on one link and silently produce a URL that no route matches. Each
 * dynamic segment is percent-encoded: category and patient ids come from the API and the customer
 * from the URL, so none is guaranteed path-safe.
 *
 * These live in the app rather than in the shell because the paths beneath the mount point are the
 * app's own — the shell owns the prefix and nothing below it. The mount point itself is built from
 * {@link SCOUT_APP_ID}, so the app names its segment once and the shell mounts it under the same
 * value (see `shell/app/registry.ts`).
 *
 * The category and patient paths take the query string as well, because within one category the
 * list's own state — which page, which search term, which filters — lives there (see
 * `usePatientsPage` / `usePatientFilters`). A link that drops it does not merely lose cosmetic
 * state: the row it points at is a row of *that* page, so opening it against the default first page
 * names a patient the list no longer holds, and the detail route falls back to the category. Every
 * link within a category therefore carries the query string forward; only a link that deliberately
 * starts a new result set (switching category, entering the app) omits it.
 */

/** Normalises a query string to the empty string or a single leading `?`. */
const querySuffix = (search: string): string => {
  const trimmed = search.startsWith('?') ? search.slice(1) : search
  return trimmed === '' ? '' : `?${trimmed}`
}

/**
 * Scout's app segment beneath the customer.
 *
 * The literal the shell mounts this app under, and the same string as its registry id. Exported so
 * the paths below and the registry agree by construction rather than by two matching literals.
 */
export const SCOUT_APP_ID = 'scout'

/** Scout's mount point: the app's root under a customer. */
export const getScoutRootPath = (customer: string): string =>
  `/c/${encodeURIComponent(customer)}/${SCOUT_APP_ID}`

/**
 * The patient list for one gap category, optionally keeping the list state the given query string
 * carries.
 */
export const getCategoryPath = (
  customer: string,
  categoryId: string,
  search = '',
): string =>
  `${getScoutRootPath(customer)}/care-gaps/${encodeURIComponent(categoryId)}${querySuffix(search)}`

/**
 * The patient list with one patient's detail flyout open over it, keeping the list state the given
 * query string carries so the row is opened against the page it was rendered on.
 */
export const getPatientPath = (
  customer: string,
  categoryId: string,
  patientId: string,
  search = '',
): string =>
  `${getCategoryPath(customer, categoryId)}/patients/${encodeURIComponent(patientId)}${querySuffix(search)}`
